import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { api } from "@/lib/api";
import { toast } from "sonner";
import SavedFoodsQuickLog from "@/components/SavedFoodsQuickLog";

const VERDICT_STYLE = {
  good: { bg: "#E8F0E9", fg: "#3B5A3E", label: "Good pick" },
  moderate: { bg: "#FBEFEA", fg: "#A65541", label: "Eat in moderation" },
  bad: { bg: "#FBE5DE", fg: "#A33D26", label: "Better swap available" },
};

export default function Scan() {
  const [tab, setTab] = useState("barcode");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [manualCode, setManualCode] = useState("");
  const [freeTier, setFreeTier] = useState(null);

  const refreshFreeTier = async () => {
    try {
      const { data } = await api.get("/billing/free-tier");
      setFreeTier(data);
    } catch { /* ignore — banner just hides */ }
  };

  useEffect(() => { refreshFreeTier(); }, []);

  // Barcode scanner state
  const scannerRef = useRef(null);
  const [scannerActive, setScannerActive] = useState(false);
  const [scannerError, setScannerError] = useState(null);

  const stopScanner = async () => {
    if (scannerRef.current) {
      try { await scannerRef.current.stop(); } catch { /* ignore */ }
      try { scannerRef.current.clear(); } catch { /* ignore */ }
      scannerRef.current = null;
    }
    setScannerActive(false);
  };

  useEffect(() => () => { stopScanner(); }, []);

  const startScanner = async () => {
    setScannerError(null);
    setResult(null);
    try {
      const qr = new Html5Qrcode("ff-barcode-region", { verbose: false });
      scannerRef.current = qr;
      setScannerActive(true);
      await qr.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 280, height: 160 } },
        (decoded) => { handleBarcode(decoded); },
        () => { /* ignored frame errors */ }
      );
    } catch (e) {
      setScannerError(e?.message || "Could not start camera. Allow camera access and try again — or enter the barcode manually below.");
      setScannerActive(false);
    }
  };

  const handleBarcode = async (code) => {
    await stopScanner();
    setBusy(true);
    setResult(null);
    setLogged(false);
    try {
      const { data } = await api.get(`/scan/barcode/${encodeURIComponent(code)}`);
      setResult({ kind: "barcode", ...data });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Product not found");
    } finally {
      setBusy(false);
      refreshFreeTier();
    }
  };

  const submitManual = (e) => {
    e.preventDefault();
    const code = manualCode.trim();
    if (!/^\d{6,14}$/.test(code)) {
      toast.error("Enter a 6–14 digit barcode");
      return;
    }
    handleBarcode(code);
  };

  // Photo flow
  const fileInputRef = useRef(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [logging, setLogging] = useState(false);
  const [logged, setLogged] = useState(false);

  const logTodayCalories = async () => {
    if (!result) return;
    const kcal = result.kind === "barcode"
      ? (result.kcal_per_serving ?? result.kcal_per_100g)
      : result.estimated_kcal;
    if (kcal == null) {
      toast.error("No kcal estimate to log");
      return;
    }
    setLogging(true);
    try {
      const { data } = await api.post("/calorie-log", {
        name: result.name || "Scanned food",
        kcal: Math.round(kcal),
        source: result.kind === "barcode" ? "barcode" : "photo",
      });
      setLogged(true);
      const remaining = data.remaining_kcal;
      const target = data.daily_target_kcal;
      const msg = target != null && remaining != null
        ? (remaining < 0
            ? `+${Math.round(kcal)} kcal · ${Math.abs(remaining)} over your ${target} target`
            : `+${Math.round(kcal)} kcal · ${Math.max(0, remaining)} kcal left today`)
        : `+${Math.round(kcal)} kcal added to today`;
      toast.success(msg, {
        duration: 5000,
        action: { label: "View planner", onClick: () => { window.location.href = "/app/planner"; } },
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not log calories");
    } finally {
      setLogging(false);
    }
  };

  const onPhotoChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const isHeic = /heic|heif/i.test(f.type) || /\.(heic|heif)$/i.test(f.name || "");
    if (isHeic) {
      toast.error("iPhone HEIC photos aren't supported yet — in your iPhone camera Settings, switch Formats to 'Most Compatible' (JPEG), or share via the Files app as JPEG.", { duration: 8000 });
      return;
    }
    if (!/^image\/(jpeg|png|webp|jpg)$/i.test(f.type)) {
      toast.error(`Unsupported image type (${f.type || "unknown"}). Use a JPEG, PNG or WEBP.`);
      return;
    }
    if (f.size > 6 * 1024 * 1024) {
      toast.error("Image too large (max 6MB) — please pick a smaller photo");
      return;
    }
    // Convert + downscale to keep payload small
    let b64;
    try {
      b64 = await fileToResizedBase64(f, 1280, 0.85);
    } catch {
      toast.error("Couldn't read the photo — please try a different one");
      return;
    }
    setPhotoPreview(`data:image/jpeg;base64,${b64}`);
    setResult(null);
    setLogged(false);
    setBusy(true);
    try {
      const { data } = await api.post("/scan/photo", { image_base64: b64 });
      setResult({ kind: "photo", ...data });
    } catch (err) {
      const code = err?.response?.status;
      const detail = err?.response?.data?.detail;
      const detailStr = typeof detail === "string" ? detail : "";
      let msg;
      if (code === 422) {
        msg = "We couldn't recognise food in that photo. Try a clearer, well-lit close-up of the plate.";
      } else if (/could not process image|image too small|low.*quality/i.test(detailStr)) {
        msg = "The photo was too small or low quality for the AI to read. Try a sharper close-up.";
      } else if (code === 413) {
        msg = "The photo was too large — try a smaller image.";
      } else if (code === 502) {
        msg = "The AI service had a hiccup — please try again in a moment.";
      } else if (code === 401) {
        msg = "Please sign in again";
      } else {
        msg = detailStr || "Could not analyse the photo";
      }
      toast.error(msg, { duration: 6000 });
    } finally {
      setBusy(false);
      refreshFreeTier();
    }
  };

  return (
    <div className="space-y-8 ff-fade-in">
      <header>
        <div className="ff-label">Scan</div>
        <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight mt-2" style={{ fontFamily: "Outfit, sans-serif" }}>
          What&apos;s in this food?
        </h1>
        <p className="text-[#5C6B61] mt-2 max-w-xl">
          Scan a packaged-product barcode or snap any meal — we&apos;ll estimate calories and tell you if it&apos;s a good shout for your goals.
        </p>
      </header>

      <SavedFoodsQuickLog onPickResult={(r) => { setResult(r); setLogged(false); }} />

      <div className="inline-flex bg-[#F3F0EA] rounded-full p-1" data-testid="scan-tabs">
        <button
          onClick={() => { setTab("barcode"); setResult(null); stopScanner(); }}
          data-testid="scan-tab-barcode"
          className={`px-5 py-2 rounded-full text-sm font-medium transition ${tab === "barcode" ? "bg-white shadow-sm text-[#3B5A3E]" : "text-[#5C6B61]"}`}
        >📊 Barcode</button>
        <button
          onClick={() => { setTab("photo"); setResult(null); stopScanner(); }}
          data-testid="scan-tab-photo"
          className={`px-5 py-2 rounded-full text-sm font-medium transition ${tab === "photo" ? "bg-white shadow-sm text-[#3B5A3E]" : "text-[#5C6B61]"}`}
        >📷 Photo</button>
      </div>

      {freeTier && freeTier.is_pro === false && (
        <FreeScanBanner tab={tab} freeTier={freeTier} />
      )}

      <div className="grid lg:grid-cols-[1.1fr_1fr] gap-8">
        <section className="ff-card p-6 md:p-8">
          {tab === "barcode" ? (
            <BarcodeUI
              active={scannerActive}
              error={scannerError}
              onStart={startScanner}
              onStop={stopScanner}
              manualCode={manualCode}
              setManualCode={setManualCode}
              onManualSubmit={submitManual}
              busy={busy}
            />
          ) : (
            <PhotoUI
              fileInputRef={fileInputRef}
              preview={photoPreview}
              onPhotoChange={onPhotoChange}
              busy={busy}
            />
          )}
        </section>

        <section data-testid="scan-result">
          {busy && <div className="ff-card p-8 text-[#5C6B61]">Analysing…</div>}
          {!busy && result && (
            <ResultCard
              result={result}
              onLog={logTodayCalories}
              logging={logging}
              logged={logged}
            />
          )}
          {!busy && !result && (
            <div className="ff-card p-8 text-center text-[#5C6B61]">
              <div className="text-5xl mb-3">{tab === "barcode" ? "📊" : "📷"}</div>
              <p className="text-sm">
                {tab === "barcode"
                  ? "Point the camera at a product barcode, or type one in. We'll look it up on Open Food Facts."
                  : "Take a clear photo of your meal. We'll estimate kcal and rate the choice for you."}
              </p>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function BarcodeUI({ active, error, onStart, onStop, manualCode, setManualCode, onManualSubmit, busy }) {
  return (
    <div>
      <div className="ff-label mb-2">Camera scan</div>
      <div
        id="ff-barcode-region"
        data-testid="barcode-region"
        className="w-full aspect-video rounded-2xl bg-[#F3F0EA] overflow-hidden border border-[#E5E2DA] flex items-center justify-center text-[#8B9690] text-sm"
      >
        {!active && "Camera off"}
      </div>
      <div className="flex gap-2 mt-3">
        {!active ? (
          <button onClick={onStart} disabled={busy} data-testid="start-barcode-scan-btn" className="ff-btn-primary disabled:opacity-60">
            🎥 Start camera
          </button>
        ) : (
          <button onClick={onStop} data-testid="stop-barcode-scan-btn" className="ff-btn-ghost border border-[#E5E2DA]">Stop</button>
        )}
      </div>
      {error && <p className="text-xs text-[#C36952] mt-2">{error}</p>}

      <div className="ff-label mt-8 mb-2">Or type the barcode</div>
      <form onSubmit={onManualSubmit} className="flex gap-2">
        <input
          data-testid="manual-barcode-input"
          className="ff-input flex-1"
          placeholder="5449000000996"
          inputMode="numeric"
          value={manualCode}
          onChange={(e) => setManualCode(e.target.value.replace(/\D/g, "").slice(0, 14))}
        />
        <button type="submit" disabled={busy} data-testid="lookup-barcode-btn" className="ff-btn-primary disabled:opacity-60">
          Look up
        </button>
      </form>
      <p className="text-xs text-[#8B9690] mt-3">
        Powered by the open <a className="underline" href="https://world.openfoodfacts.org" target="_blank" rel="noreferrer">Open Food Facts</a> database. 3M+ products.
      </p>
    </div>
  );
}

function PhotoUI({ fileInputRef, preview, onPhotoChange, busy }) {
  // Second hidden input forces the camera; the original opens the photo library.
  const cameraInputRef = useRef(null);
  return (
    <div>
      <div className="ff-label mb-2">Snap your food</div>
      <div className="w-full aspect-video rounded-2xl bg-[#F3F0EA] overflow-hidden border border-[#E5E2DA] flex items-center justify-center">
        {preview ? (
          <img src={preview} alt="" className="w-full h-full object-cover" data-testid="photo-preview" />
        ) : (
          <div className="text-[#8B9690] text-sm text-center px-6">A clear, well-lit photo gives the best estimate.</div>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2 mt-3">
        {/* Camera — accept image/* so iOS camera (HEIC) can launch; we validate format on change. */}
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={onPhotoChange}
          className="hidden"
          data-testid="camera-input"
        />
        <button
          onClick={() => cameraInputRef.current?.click()}
          disabled={busy}
          data-testid="take-photo-btn"
          className="ff-btn-primary disabled:opacity-60"
        >📷 Take photo</button>

        {/* Photo library */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={onPhotoChange}
          className="hidden"
          data-testid="photo-input"
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={busy}
          data-testid="upload-photo-btn"
          className="rounded-full px-5 py-3 text-sm font-medium bg-white border border-[#E5E2DA] text-[#2A332C] hover:border-[#3B5A3E] transition disabled:opacity-60"
        >🖼️ Upload photo</button>
      </div>
      <p className="text-xs text-[#8B9690] mt-3">Analysed by Claude vision. Estimates only — not medical advice.</p>
    </div>
  );
}

function ResultCard({ result, onLog, logging, logged }) {
  const v = result.verdict || "moderate";
  const style = VERDICT_STYLE[v] || VERDICT_STYLE.moderate;
  const kcalAvailable = result.kind === "barcode"
    ? (result.kcal_per_serving ?? result.kcal_per_100g) != null
    : result.estimated_kcal != null;
  return (
    <div className="ff-card p-6 md:p-8" data-testid="scan-result-card">
      <div className="flex items-start gap-4">
        {result.image && (
          <img src={result.image} alt="" className="w-20 h-20 rounded-xl object-cover flex-none" />
        )}
        <div className="flex-1 min-w-0">
          <div className="ff-label">{result.kind === "barcode" ? "Packaged product" : "Photo analysis"}</div>
          <h2 className="text-2xl font-semibold mt-1 truncate" style={{ fontFamily: "Outfit, sans-serif" }}>
            {result.name || "Unknown"}
          </h2>
          {result.brand && <p className="text-sm text-[#8B9690] mt-0.5">{result.brand}</p>}
          {result.portion && <p className="text-sm text-[#5C6B61] mt-1">{result.portion}</p>}
        </div>
      </div>

      <div
        className="mt-5 rounded-2xl p-4"
        style={{ background: style.bg, color: style.fg }}
        data-testid="scan-verdict"
      >
        <div className="text-xs font-medium uppercase tracking-wide opacity-80">{style.label}</div>
        <p className="text-sm mt-1">{result.reason}</p>
        {result.healthier_swap && (
          <p className="text-xs mt-2 opacity-90">💡 {result.healthier_swap}</p>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-5">
        {result.kind === "barcode" ? (
          <>
            <Stat label="Per 100g" value={result.kcal_per_100g != null ? `${result.kcal_per_100g} kcal` : "—"} />
            <Stat label="Per serving" value={result.kcal_per_serving != null ? `${result.kcal_per_serving} kcal` : "—"} />
            <Stat label="Protein /100g" value={result.protein_g_100g != null ? `${result.protein_g_100g}g` : "—"} />
            <Stat label="Carbs /100g" value={result.carbs_g_100g != null ? `${result.carbs_g_100g}g` : "—"} />
            <Stat label="Fat /100g" value={result.fat_g_100g != null ? `${result.fat_g_100g}g` : "—"} />
            <Stat label="Sugar /100g" value={result.sugar_g_100g != null ? `${result.sugar_g_100g}g` : "—"} />
            <Stat label="Salt /100g" value={result.salt_g_100g != null ? `${result.salt_g_100g}g` : "—"} />
          </>
        ) : (
          <>
            <Stat label="Estimated" value={result.estimated_kcal != null ? `${result.estimated_kcal} kcal` : "—"} />
            <Stat label="Protein" value={result.macros?.protein_g != null ? `${result.macros.protein_g}g` : "—"} />
            <Stat label="Carbs" value={result.macros?.carbs_g != null ? `${result.macros.carbs_g}g` : "—"} />
            <Stat label="Fat" value={result.macros?.fat_g != null ? `${result.macros.fat_g}g` : "—"} />
            <Stat label="Sugar" value={result.macros?.sugar_g != null ? `${result.macros.sugar_g}g` : "—"} />
            <Stat label="Salt" value={result.macros?.salt_g != null ? `${result.macros.salt_g}g` : "—"} />
          </>
        )}
      </div>

      {kcalAvailable && (
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2 mt-5">
          <button
            onClick={onLog}
            disabled={logging || logged}
            data-testid="log-todays-calories-btn"
            className={`rounded-full px-5 py-3 text-sm font-medium transition disabled:opacity-60 ${
              logged
                ? "bg-[#E8F0E9] text-[#3B5A3E] border border-[#cfe0d0]"
                : "bg-[#3B5A3E] text-white hover:bg-[#2c4530]"
            }`}
          >
            {logged ? "✓ Logged to today" : logging ? "Logging…" : "➕ Log to today's calories"}
          </button>
          <SaveForLaterButton result={result} />
        </div>
      )}

      {result.kind === "barcode" && result.nutriscore_grade && (
        <p className="text-xs text-[#8B9690] mt-4">
          Nutri-Score: <span className="font-semibold uppercase">{result.nutriscore_grade}</span>
          {result.nova_group ? ` · NOVA processing group ${result.nova_group}` : ""}
        </p>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-xl bg-[#F3F0EA] p-3">
      <div className="text-[10px] uppercase tracking-wide text-[#8B9690]">{label}</div>
      <div className="text-sm font-semibold text-[#2A332C] mt-1">{value}</div>
    </div>
  );
}

// ---------------------------- Save for later (⭐) ----------------------------
function SaveForLaterButton({ result }) {
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setSaved(false); }, [result]);

  const kcal = result.kind === "barcode"
    ? (result.kcal_per_serving ?? result.kcal_per_100g)
    : result.estimated_kcal;

  const save = async () => {
    if (kcal == null) return toast.error("No kcal to save");
    setBusy(true);
    try {
      await api.post("/saved-foods", {
        name: result.name || "Scanned food",
        kcal: Math.round(kcal),
        kind: result.kind,
        source: result.kind === "barcode" ? "barcode" : "photo",
        image: result.image || null,
        portion: result.portion || result.serving_size || null,
        brand: result.brand || null,
        barcode: result.barcode || null,
        kcal_per_100g: result.kcal_per_100g ?? null,
        kcal_per_serving: result.kcal_per_serving ?? null,
        macros: result.macros || null,
      });
      setSaved(true);
      toast.success("Saved — quick-log it any time from the Saved foods panel ⭐");
      window.dispatchEvent(new CustomEvent("saved-foods:changed"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      onClick={save}
      disabled={busy || saved || kcal == null}
      data-testid="save-for-later-btn"
      title="Save to your foods library"
      className={`rounded-full px-4 py-3 text-sm font-medium transition disabled:opacity-60 border ${
        saved
          ? "bg-[#FFF6E3] text-[#B07F19] border-[#F0E2BD]"
          : "bg-white text-[#2A332C] border-[#E5E2DA] hover:border-[#3B5A3E]"
      }`}
    >
      {saved ? "★ Saved" : busy ? "Saving…" : "☆ Save for later"}
    </button>
  );
}

function FreeScanBanner({ tab, freeTier }) {
  const photo = freeTier.photo_scans || { used: 0, limit: 5 };
  const barcode = freeTier.barcode_scans || { used: 0, limit: 5 };
  const active = tab === "photo" ? photo : barcode;
  const other = tab === "photo" ? barcode : photo;
  const label = tab === "photo" ? "photo scans" : "barcode scans";
  const otherLabel = tab === "photo" ? "barcode" : "photo";
  const remaining = Math.max(0, (active.limit || 0) - (active.used || 0));
  const isMaxed = remaining === 0;

  const triggerUpsell = () => {
    try {
      window.dispatchEvent(new CustomEvent("pro-upsell", { detail: {
        code: tab === "photo" ? "pro_limit_photo_scans" : "pro_limit_barcode_scans",
        message: isMaxed
          ? `You've used all ${active.limit} free ${label}. Start your 24-hour Pro trial for unlimited scans.`
          : `Free plan includes ${active.limit} lifetime ${label}. Go Pro for unlimited.`,
        feature: tab === "photo" ? "Unlimited photo scans" : "Unlimited barcode scans",
      } }));
    } catch { /* ignore */ }
  };

  return (
    <div
      onClick={triggerUpsell}
      role="button"
      tabIndex={0}
      data-testid="free-scan-banner"
      className={`rounded-2xl px-4 py-3 text-sm cursor-pointer flex items-center gap-3 transition border ${
        isMaxed
          ? "bg-[#FCF0EC] border-[#E8C4B8] text-[#A33D26]"
          : "bg-[#FAF6EE] border-[#E0D5C2] text-[#5C6B61] hover:border-[#3B5A3E]"
      }`}
    >
      <span className="text-lg flex-none">{isMaxed ? "🔒" : (tab === "photo" ? "📷" : "📊")}</span>
      <span className="flex-1 min-w-0">
        {isMaxed ? (
          <>You&apos;ve used all <strong data-testid="free-scan-banner-count">{active.used} / {active.limit}</strong> free {label}. <span className="underline">Start your 24-hour free trial</span> for unlimited scans.</>
        ) : (
          <>
            <strong data-testid="free-scan-banner-count">{active.used} / {active.limit}</strong> free {label} used
            <span className="text-[#8B9690]"> · {other.used} / {other.limit} {otherLabel}</span>. <span className="underline">Upgrade to Pro</span> for unlimited.
          </>
        )}
      </span>
    </div>
  );
}



// File → resized base64 (no data: prefix)
function fileToResizedBase64(file, maxDim, quality) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > maxDim || height > maxDim) {
          const ratio = Math.min(maxDim / width, maxDim / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        const dataUrl = canvas.toDataURL("image/jpeg", quality);
        resolve(dataUrl.split(",")[1]);
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
